// Clues one cell outside the grid: sandwich, X-sum, skyscraper, frame sum and battlefield
// numbers (Pack 9's outside digits, increasing runs and up-to-N sums the same way), and little
// killers (a number with a short arrow along its diagonal).
import type { ConstraintEntry, PuzzleDoc } from '@zinc/shared';
import { centre, isRc, num, ofType, styleOf, text } from './geom';
import { FONT, INK, LITTLE_KILLER, OUTSIDE } from './style';

const NUMBER_TYPES = [
  'sandwich',
  'x-sum',
  'skyscraper',
  'frame-sum',
  'battlefield',
  'numbered-room',
  'outside-digits',
  'increasing-run',
  'up-to-sum',
];

function shownValue(e: ConstraintEntry): string | null {
  const label = text(e, 'label');
  if (label) return label;
  if (e.type === 'outside-digits')
    return Array.isArray(e.digits) && e.digits.length ? e.digits.join('') : null;
  const v = num(
    e,
    e.type === 'skyscraper'
      ? 'count'
      : e.type === 'numbered-room' || e.type === 'increasing-run'
        ? 'value'
        : 'sum',
  );
  return v === null ? null : String(v);
}

function LittleKiller({ e }: { e: ConstraintEntry }) {
  const clue = e.clue;
  const dir = e.direction;
  if (!isRc(clue) || !isRc(dir)) return null;
  const shown = shownValue(e);
  const [cx, cy] = centre(clue);
  // Unit diagonal in SVG axes (x = column, y = row).
  const len = Math.hypot(dir[0], dir[1]) || 1;
  const ux = dir[1] / len;
  const uy = dir[0] / len;
  const { color, width } = styleOf(e, LITTLE_KILLER);
  // The arrow runs towards the corner of the clue cell that touches the diagonal.
  const from = 9;
  const to = 22;
  const tx = cx + ux * to;
  const ty = cy + uy * to;
  const h = LITTLE_KILLER.head;
  const head = `M${tx - ux * h * 1.3 - uy * h} ${ty - uy * h * 1.3 + ux * h}L${tx} ${ty}L${tx - ux * h * 1.3 + uy * h} ${ty - uy * h * 1.3 - ux * h}`;
  return (
    <g data-constraint="little-killer">
      {shown && (
        <text
          x={cx - ux * 3}
          y={cy - uy * 3 + 0.5}
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily={FONT}
          fontSize={LITTLE_KILLER.size}
          fontWeight={LITTLE_KILLER.weight}
          fill={INK}
        >
          {shown}
        </text>
      )}
      <path
        d={`M${cx + ux * from} ${cy + uy * from}L${tx} ${ty}${head}`}
        fill="none"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

export function OutsideClues({ doc }: { doc: PuzzleDoc }) {
  return (
    <g>
      {ofType(doc, ...NUMBER_TYPES).map((e, i) => {
        const clue = e.clue;
        const shown = shownValue(e);
        if (!isRc(clue) || shown === null) return null;
        const [x, y] = centre(clue);
        const { color } = styleOf(e, { color: INK, width: 1 });
        return (
          <text
            key={`o${i}`}
            data-constraint={e.type}
            x={x}
            y={y + 1}
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={FONT}
            fontSize={shown.length > 2 ? OUTSIDE.size * 0.8 : OUTSIDE.size}
            fontWeight={OUTSIDE.weight}
            fill={color}
          >
            {shown}
          </text>
        );
      })}
      {ofType(doc, 'little-killer').map((e, i) => (
        <LittleKiller key={`l${i}`} e={e} />
      ))}
    </g>
  );
}
